import {useEffect, useRef, useState} from 'react';
import noop from '@tinkoff/utils/function/noop';
import {useIntl} from 'react-intl';
import classnames from 'classnames';

import {ActionButton} from '../../../molecules/icon-button';
import {CheckIcon} from '../../../icons/check';
import {LinkIcon} from '../../../icons/link';

import type {EmbeddedImage} from './embedded-image';
import styles from './embedded-image.module.css';

type Props = Pick<React.ComponentProps<typeof EmbeddedImage>, 'onCopy' | 'getFilePublicUrl'>;

const COPY_DELAY = 1500;

export const CopyAction = ({
    onCopy = noop,
    getFilePublicUrl,
    fileId,
}: Pick<Props, 'onCopy' | 'getFilePublicUrl'> & { fileId: string }) => {
    const {formatMessage} = useIntl();
    const copyButtonLabel = formatMessage({id: 'single_image_view.copy_link_tooltip', defaultMessage: 'Copy link'});
    const [isRecentlyCopied, setRecentlyCopied] = useState(false);
    const [publicUrl, setPublicUrl] = useState<string | undefined>();
    const timeoutRef = useRef<NodeJS.Timeout | undefined>();

    useEffect(() => {
        getFilePublicUrl?.(fileId).then(setPublicUrl);
    }, [getFilePublicUrl, fileId]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    if (!publicUrl) {
        return null;
    }

    const handlePress = () => {
        setRecentlyCopied(true);
        onCopy(publicUrl);
        timeoutRef.current = setTimeout(() => {
            setRecentlyCopied(false);
        }, COPY_DELAY);
    };

    return (
        <ActionButton
            aria-label={copyButtonLabel}
            tooltip={copyButtonLabel}
            isDisabled={isRecentlyCopied}
            onPress={handlePress}
            className={classnames({
                [styles.copied]: isRecentlyCopied,
            })}
        >
            {isRecentlyCopied ? <CheckIcon width={20} height={20} /> : <LinkIcon width={20} height={20} />}
        </ActionButton>
    );
};
